import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Loader2, Mail, Upload, KeyRound } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { adminMe, createPaper, updatePaper, deletePaper, createTopic, updateTopic, deleteTopic, createWriting, updateWriting, deleteWriting, updateSiteSettings, listContacts, deleteContact, changePassword, uploadPdf } from "@/lib/adminApi";
import { getPapers, getTopics, getWritings, getSiteSettings } from "@/lib/api";

const TABS = ["papers", "topics", "writings", "site", "messages"];

const SCHEMAS = {
  papers: [
    { k: "title", label: "Title", type: "text" },
    { k: "slug", label: "Slug", type: "text" },
    { k: "abstract", label: "Abstract", type: "textarea" },
    { k: "year", label: "Year", type: "text" },
    { k: "status", label: "Status (Published / Preprint / Living Document / In Progress / Upcoming)", type: "text" },
    { k: "pdfUrl", label: "PDF file", type: "pdf" },
    { k: "topicTags", label: "Topic tags (comma separated)", type: "list" },
    { k: "featured", label: "Featured", type: "switch" },
    { k: "order", label: "Order", type: "number" },
  ],
  topics: [
    { k: "name", label: "Name", type: "text" },
    { k: "description", label: "Description", type: "textarea" },
    { k: "relatedPaperSlugs", label: "Related paper slugs (comma separated)", type: "list" },
    { k: "order", label: "Order", type: "number" },
  ],
  writings: [
    { k: "title", label: "Title", type: "text" },
    { k: "sourceType", label: "Source (Substack / X)", type: "text" },
    { k: "externalUrl", label: "External URL", type: "text" },
    { k: "summary", label: "Summary", type: "textarea" },
    { k: "publishDate", label: "Publish date", type: "text" },
    { k: "order", label: "Order", type: "number" },
  ],
};

const EMPTY = {
  papers: { title: "", slug: "", abstract: "", year: "", status: "Preprint", pdfUrl: "", topicTags: [], featured: false, order: 0 },
  topics: { name: "", description: "", relatedPaperSlugs: [], order: 0 },
  writings: { title: "", sourceType: "Substack", externalUrl: "", summary: "", publishDate: "", order: 0 },
};

const PdfField = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadPdf(file);
      onChange(res.url);
      toast.success(`Uploaded ${res.filename}`);
    } catch {
      toast.error("Upload failed (PDF only, max 15MB).");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/papers/name.pdf or upload below"
        className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-sans text-sm text-white outline-none transition-colors focus:border-plasma-teal"
        data-testid="field-pdfUrl"
      />
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-plasma-teal/40 bg-plasma-teal/10 px-3 py-2 font-mono text-xs uppercase tracking-widest text-plasma-teal transition-colors hover:bg-plasma-teal/20" data-testid="pdf-upload-label">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {busy ? "Uploading…" : "Upload PDF"}
        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onFile} disabled={busy} data-testid="pdf-upload-input" />
      </label>
      {value && <p className="truncate font-mono text-[10px] text-slate-500">{value}</p>}
    </div>
  );
};

const Field = ({ field, value, onChange }) => {
  const base = "w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-sans text-sm text-white outline-none transition-colors focus:border-plasma-teal";
  if (field.type === "pdf")
    return <PdfField value={value} onChange={onChange} />;
  if (field.type === "textarea")
    return <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${base} resize-none`} data-testid={`field-${field.k}`} />;
  if (field.type === "switch")
    return (
      <button type="button" onClick={() => onChange(!value)} data-testid={`field-${field.k}`}
        className={`relative h-6 w-11 rounded-full transition-colors ${value ? "bg-plasma-teal" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    );
  if (field.type === "number")
    return <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={base} data-testid={`field-${field.k}`} />;
  if (field.type === "list")
    return <input value={Array.isArray(value) ? value.join(", ") : value ?? ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={base} data-testid={`field-${field.k}`} />;
  return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${field.k}`} />;
};

const ChangePasswordCard = () => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await changePassword(cur, next);
      toast.success("Password updated.");
      setCur(""); setNext("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password.");
    } finally {
      setBusy(false);
    }
  };
  const inp = "w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-sans text-sm text-white outline-none transition-colors focus:border-plasma-teal";
  return (
    <div className="mt-12 max-w-md rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid="change-password-card">
      <div className="mb-5 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-plasma-teal" strokeWidth={1.5} />
        <h3 className="font-display text-xl font-light">Change password</h3>
      </div>
      <label className="overline mb-2 block">Current password</label>
      <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={`${inp} mb-4`} data-testid="cp-current" />
      <label className="overline mb-2 block">New password</label>
      <input type="password" value={next} onChange={(e) => setNext(e.target.value)} className={`${inp} mb-6`} data-testid="cp-new" />
      <button onClick={submit} disabled={busy} className="btn-plasma disabled:opacity-60" data-testid="cp-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" strokeWidth={1.5} />} Update password
      </button>
    </div>
  );
};

export default function Admin() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("papers");
  const [papers, setPapers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [writings, setWritings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // {type, item}
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    const [p, t, w, s] = await Promise.all([getPapers(), getTopics(), getWritings(), getSiteSettings()]);
    setPapers(p); setTopics(t); setWritings(w); setSettings(s);
    try { setContacts(await listContacts()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("rsp_token")) { nav("/admin/login"); return; }
    adminMe()
      .then(() => { setReady(true); refresh(); })
      .catch(() => { localStorage.removeItem("rsp_token"); nav("/admin/login"); });
  }, [nav, refresh]);

  const logout = () => { localStorage.removeItem("rsp_token"); nav("/admin/login"); };

  const openNew = (type) => setEditing({ type, item: { ...EMPTY[type] }, isNew: true });
  const openEdit = (type, item) => setEditing({ type, item: { ...item }, isNew: false });

  const save = async () => {
    const { type, item, isNew } = editing;
    setSaving(true);
    try {
      const api = {
        papers: [createPaper, updatePaper],
        topics: [createTopic, updateTopic],
        writings: [createWriting, updateWriting],
      }[type];
      if (isNew) await api[0](item);
      else await api[1](item.id, item);
      toast.success(`${type.slice(0, -1)} saved.`);
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Save failed. Check the fields.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (type, item) => {
    if (!window.confirm(`Delete "${item.title || item.name}"?`)) return;
    const del = { papers: deletePaper, topics: deleteTopic, writings: deleteWriting }[type];
    await del(item.id);
    toast.success("Deleted.");
    refresh();
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Delete message from ${c.name}?`)) return;
    await deleteContact(c.id);
    toast.success("Message deleted.");
    refresh();
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await updateSiteSettings(settings);
      toast.success("Site settings saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!ready) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-plasma-teal" />
    </div>
  );

  const lists = { papers, topics, writings };

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="grain" />
      <header className="glass sticky top-0 z-30 border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="h-1.5 w-1.5 rounded-full bg-plasma-teal shadow-[0_0_12px_#1DE9B6]" />
            <span className="font-display text-lg">Resonant Spark — Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-plasma-teal" data-testid="admin-view-site">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-plasma-teal" data-testid="admin-logout">
              Logout <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex flex-wrap gap-2 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`border-b-2 px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${tab === t ? "border-plasma-teal text-plasma-teal" : "border-transparent text-slate-400 hover:text-white"}`}>
              {t}{t === "messages" && contacts.length ? ` (${contacts.length})` : ""}
            </button>
          ))}
        </div>

        {["papers", "topics", "writings"].includes(tab) && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-3xl font-light capitalize">{tab}</h2>
              <button onClick={() => openNew(tab)} className="btn-plasma !py-2 !px-4 text-xs" data-testid={`add-${tab}`}>
                <Plus className="h-4 w-4" /> Add {tab.slice(0, -1)}
              </button>
            </div>
            <div className="space-y-3">
              {lists[tab].map((item) => (
                <div key={item.id} className="glass-card flex items-start justify-between gap-4 rounded-lg p-5" data-testid={`row-${tab}-${item.slug || item.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl text-white">{item.title || item.name}</span>
                      {item.status && <span className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-400">{item.status}</span>}
                      {item.featured && <span className="font-mono text-[10px] uppercase text-gold">featured</span>}
                    </div>
                    <p className="mt-1 line-clamp-1 font-sans text-sm text-slate-400">{item.abstract || item.description || item.summary}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => openEdit(tab, item)} className="rounded-md border border-white/10 p-2 text-slate-300 hover:border-plasma-teal/40 hover:text-plasma-teal" data-testid={`edit-${item.slug || item.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(tab, item)} className="rounded-md border border-white/10 p-2 text-slate-300 hover:border-red-500/40 hover:text-red-400" data-testid={`delete-${item.slug || item.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "site" && settings && (
          <div className="max-w-2xl">
            <h2 className="mb-6 font-display text-3xl font-light">Site settings</h2>
            <div className="space-y-5">
              {[["tagline", "Tagline"], ["heroImage", "Hero image URL"], ["contactEmail", "Contact email"], ["atlasUrl", "Atlas URL"]].map(([k, label]) => (
                <div key={k}>
                  <label className="overline mb-2 block">{label}</label>
                  <input value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} data-testid={`site-${k}`}
                    className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-sans text-sm text-white outline-none focus:border-plasma-teal" />
                </div>
              ))}
              {["substack", "x", "academia"].map((k) => (
                <div key={k}>
                  <label className="overline mb-2 block">{k} link</label>
                  <input value={settings.socialLinks?.[k] || ""} onChange={(e) => setSettings({ ...settings, socialLinks: { ...settings.socialLinks, [k]: e.target.value } })} data-testid={`site-social-${k}`}
                    className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 font-sans text-sm text-white outline-none focus:border-plasma-teal" />
                </div>
              ))}
              <button onClick={saveSettings} disabled={saving} className="btn-plasma disabled:opacity-60" data-testid="site-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings
              </button>
            </div>
            <ChangePasswordCard />
          </div>
        )}

        {tab === "messages" && (
          <div>
            <h2 className="mb-6 font-display text-3xl font-light">Messages</h2>
            {contacts.length === 0 ? (
              <p className="font-sans text-sm text-slate-500">No submissions yet.</p>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c.id} className="glass-card rounded-lg p-5" data-testid={`message-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-display text-lg text-white">{c.name}</span>
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 font-mono text-xs text-plasma-teal"><Mail className="h-3 w-3" /> {c.email}</a>
                      <span className="rounded-full border border-white/15 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-400">{c.intent}</span>
                      <span className="ml-auto font-mono text-[10px] text-slate-600">{new Date(c.createdAt).toLocaleString()}</span>
                      <button onClick={() => removeContact(c)} className="rounded-md border border-white/10 p-1.5 text-slate-400 hover:border-red-500/40 hover:text-red-400" data-testid={`delete-message-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <p className="mt-3 font-sans text-sm text-slate-300">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-[#0B0D17] text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light capitalize">
              {editing?.isNew ? "New" : "Edit"} {editing?.type?.slice(0, -1)}
            </DialogTitle>
            <DialogDescription className="font-sans text-sm text-slate-400">
              Fill in the fields below, then save to publish to the live site.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {SCHEMAS[editing.type].map((f) => (
                <div key={f.k}>
                  <label className="overline mb-2 block">{f.label}</label>
                  <Field field={f} value={editing.item[f.k]} onChange={(v) => setEditing({ ...editing, item: { ...editing.item, [f.k]: v } })} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="btn-gold" data-testid="dialog-cancel">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-plasma disabled:opacity-60" data-testid="dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
